import Button, { ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import Input from '@gym-atoms/Input/Input';
import Loader from '@gym-atoms/Loader/Loader';
import { deactivateGymKeyAccessCard, issueGymKeyAccessCard } from '@gym-graphql/mutations';
import { getAccessCardHistory } from '@gym-graphql/queries';
import { MemberAccessCard } from '@gym-particles/types/Member';
import { useAppSelector } from '@gym-redux/store';
import {
  DeactivateGymKeyAccessCardMutation,
  DeactivateGymKeyAccessCardMutationVariables,
  GetAccessCardHistoryQuery,
  GetAccessCardHistoryQueryVariables,
  IssueGymKeyAccessCardMutation,
  IssueGymKeyAccessCardMutationVariables
} from '@gym-src/API';
import { API } from 'aws-amplify';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

export type GymKeyAccessCardAction = 'issue' | 'deactivate';

type RequestState = 'idle' | 'loading' | 'submitting' | 'error';

const parseActiveAccessCards = (
  cards: GetAccessCardHistoryQuery['getAccessCardHistory']
): SelectItem[] =>
  (cards || []).flatMap((card) =>
    card?.isActive && card.accessCardUID
      ? [{ label: card.accessCardUID, value: card.accessCardUID }]
      : []
  );

const GymKeyAccessCardModal = ({
  action,
  visible,
  member,
  onHide,
  onSuccess
}: GymKeyAccessCardModalProps) => {
  const { t } = useTranslation();
  const { gymId } = useParams<{ gymId: string }>();
  const currentUserId = useAppSelector((state) => state.user.userId);
  const [accessCardUID, setAccessCardUID] = useState('');
  const [activeCards, setActiveCards] = useState<SelectItem[]>([]);
  const [requestState, setRequestState] = useState<RequestState>('idle');

  const isDeactivate = action === 'deactivate';
  const isLoading = requestState === 'loading';
  const isSubmitting = requestState === 'submitting';
  const hasError = requestState === 'error';

  useEffect(() => {
    if (!visible) {
      setAccessCardUID('');
      setActiveCards([]);
      setRequestState('idle');
      return;
    }

    if (!isDeactivate) {
      return;
    }

    const loadActiveCards = async () => {
      setRequestState('loading');
      const variables: GetAccessCardHistoryQueryVariables = {
        memberId: member.memberId,
        siteLocationId: +gymId
      };

      try {
        const response = await (API.graphql({
          query: getAccessCardHistory,
          variables
        }) as Promise<{ data: GetAccessCardHistoryQuery }>);
        setActiveCards(parseActiveAccessCards(response.data.getAccessCardHistory));
        setRequestState('idle');
      } catch (error) {
        console.error('Unable to load active GymKey access cards', error);
        setRequestState('error');
      }
    };

    loadActiveCards();
  }, [isDeactivate, member.memberId, visible, gymId]);

  const closeModal = () => {
    setAccessCardUID('');
    setActiveCards([]);
    setRequestState('idle');
    onHide();
  };

  const completeRequest = () => {
    onSuccess();
    closeModal();
  };

  const issueCard = async () => {
    const variables: IssueGymKeyAccessCardMutationVariables = {
      input: {
        memberId: member.memberId,
        siteLocationId: +gymId,
        userId: currentUserId,
        accessCardUID
      }
    };
    const response = await (API.graphql({
      query: issueGymKeyAccessCard,
      variables
    }) as Promise<{ data: IssueGymKeyAccessCardMutation }>);

    return response.data.issueGymKeyAccessCard === true;
  };

  const deactivateCard = async () => {
    const variables: DeactivateGymKeyAccessCardMutationVariables = {
      memberId: member.memberId,
      siteLocationId: +gymId,
      lastModifiedBy: currentUserId,
      accessCardUID
    };
    const response = await (API.graphql({
      query: deactivateGymKeyAccessCard,
      variables
    }) as Promise<{ data: DeactivateGymKeyAccessCardMutation }>);

    return response.data.deactivateGymKeyAccessCard === true;
  };

  const submit = async () => {
    setRequestState('submitting');

    try {
      const succeeded = isDeactivate ? await deactivateCard() : await issueCard();
      succeeded ? completeRequest() : setRequestState('error');
    } catch (error) {
      console.error(`Unable to ${action} GymKey access card`, error);
      setRequestState('error');
    }
  };

  const content = useMemo(
    () =>
      isLoading ? (
        <Loader shape="rectangle" width="full" />
      ) : isDeactivate && activeCards.length === 0 ? (
        <p>{t('GYMKEY_ACCESS_CARD.NO_ACTIVE_CARD')}</p>
      ) : (
        <>
          {isDeactivate ? (
            <>
              <label className="fw-bold">{t('GYMKEY_ACCESS_CARD.ISSUED_CARD')}</label>
              <Dropdown
                value={accessCardUID}
                options={activeCards}
                onChange={(event: DropdownChangeEvent) => setAccessCardUID(event.value)}
                placeholder={t('GYMKEY_ACCESS_CARD.SELECT_ISSUED_CARD')}
              />
            </>
          ) : (
            <Input
              label={t('GYMKEY_ACCESS_CARD.ACCESS_CARD_ID')}
              value={accessCardUID}
              onChange={(event: FormEvent<HTMLInputElement>) =>
                setAccessCardUID(event.currentTarget.value)
              }
              required={true}
            />
          )}
          {hasError && <p className="p-error">{t('GYMKEY_ACCESS_CARD.ERROR')}</p>}
        </>
      ),
    [accessCardUID, activeCards, hasError, isDeactivate, isLoading, t]
  );

  const footer =
    !isLoading && (!isDeactivate || activeCards.length > 0) ? (
      <div>
        <Button
          label={t('GYMKEY_ACCESS_CARD.CANCEL')}
          variant={ButtonVariants.textonly}
          onClick={closeModal}
          disabled={isSubmitting}
        />
        <Button
          label={t(
            isDeactivate
              ? 'GYMKEY_ACCESS_CARD.DEACTIVATE_BUTTON'
              : 'GYMKEY_ACCESS_CARD.ISSUE_BUTTON'
          )}
          variant={isDeactivate ? ButtonVariants.danger : ButtonVariants.basic}
          icon={isSubmitting ? 'pi-spin pi-spinner' : ''}
          onClick={submit}
          disabled={accessCardUID.trim().length === 0 || isSubmitting}
        />
      </div>
    ) : (
      <Button label={t('GYMKEY_ACCESS_CARD.CANCEL')} onClick={closeModal} />
    );

  return (
    <DialogBox
      variant={DialogBoxVariants.long}
      dialogVisible={visible}
      dialogClosable={true}
      dialogDismissableMask={false}
      onHideCallback={closeModal}
      dialogHeader={
        <span>
          {t(
            isDeactivate ? 'GYMKEY_ACCESS_CARD.DEACTIVATE_TITLE' : 'GYMKEY_ACCESS_CARD.ISSUE_TITLE'
          )}
        </span>
      }
      dialogFooter={footer}
      content={content}
    />
  );
};

interface GymKeyAccessCardModalProps {
  action: GymKeyAccessCardAction;
  visible: boolean;
  member: MemberAccessCard;
  onHide: () => void;
  onSuccess: () => void;
}

export default GymKeyAccessCardModal;
